"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PlatformNext = void 0;
const cross_fetch_1 = __importDefault(require("cross-fetch"));
const LaunchRequest_1 = require("./LaunchRequest");
const PlatformCredentials_1 = require("./PlatformCredentials");
const RegionalLatencyMeasurement_1 = require("./RegionalLatencyMeasurement");
const ws_1 = __importDefault(require("ws"));
const Info_1 = __importDefault(require("../util/Info"));
const agent_1 = require("../agent");
const AgentConnectionStatus_1 = require("../agent/AgentConnectionStatus");
// eslint-disable-next-line @typescript-eslint/no-explicit-any
if (!global.WebSocket) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    global.WebSocket = ws_1.default;
}
const DEFAULT_ENDPOINT = 'https://api.pureweb.io';
class PlatformNext {
    get credentials() {
        return this._credentials;
    }
    set credentials(value) {
        this._credentials = value;
    }
    get agent() {
        return this._agent;
    }
    set agent(value) {
        this._agent = value;
    }
    get endpoint() {
        return this._endpoint;
    }
    set endpoint(value) {
        this._endpoint = value;
    }
    isAuthenticated() {
        return this.credentials != null;
    }
    constructor() {
        this._endpoint = DEFAULT_ENDPOINT;
        this.disconnect = () => {
            this.agent.disconnect();
        };
        this.initialize = (options) => {
            if (options.endpoint) {
                this.endpoint = options.endpoint;
            }
        };
        this.requestOptions = {
            cors: true,
            mode: 'cors'
        };
        this.requestOptions.mode = this.requestOptions.cors ? 'cors' : 'same-origin';
    }
    static get version() {
        return Info_1.default.version;
    }
    static get info() {
        return 'Version: ' + Info_1.default.version + ' ' + Info_1.default.hash + ' ' + Info_1.default.date;
    }
    /**
     * Connects to the PureWeb Platform
     * @param timeoutMs A timeout in milliseconds for how long to wait for the connection to establish. Defaults to 30s.
     * @returns A local, connected, agent
     */
    connect(timeoutMs) {
        if (!this.credentials.agent || !this.credentials.agent.isValid()) {
            throw 'Agent token not initialized;';
        }
        const result = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            let connected = false;
            try {
                this._agent = yield agent_1.LocalAgent.create(this.credentials.agent);
                this.agent.onConnection((state) => {
                    if (state === AgentConnectionStatus_1.AgentConnectionStatus.CONNECTED) {
                        connected = true;
                        resolve(this._agent);
                    }
                });
                this.agent.connect();
            }
            catch (err) {
                reject(err);
            }
            setTimeout(() => {
                if (!connected) {
                    reject(new Error('Timeout expired while connecting to PureWeb Platform'));
                }
            }, timeoutMs ? timeoutMs : 30000);
        }));
        return result;
    }
    getLatencyMeasurements() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.cachedLatencies) {
                this.cachedLatencies = yield (0, RegionalLatencyMeasurement_1.measureRegionalLatencies)();
            }
            return this.cachedLatencies;
        });
    }
    useAnonymousCredentials(projectId, environmentId) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield (0, cross_fetch_1.default)(this.endpoint +
                `/project/${projectId}/anonymous-access${environmentId ? '?environment_id=' + environmentId : ''}`, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json'
                },
                mode: this.requestOptions.mode
            });
            if (res.status === 200) {
                const response = yield res.json();
                const anonymousCredentials = new PlatformCredentials_1.PlatformCredentials();
                anonymousCredentials.fromJSON(response);
                this.credentials = anonymousCredentials;
                return anonymousCredentials;
            }
            else {
                throw new Error(`Unable to obtain anonymous Credentials ${res.status}`);
            }
        });
    }
    // Exchange the tokens for share and collaborate features
    useSessionCredentials(projectId, projectToken, environmentId) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield (0, cross_fetch_1.default)(this.endpoint + `/project/${projectId}/session`, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    Authorization: 'Bearer ' + projectToken
                },
                body: environmentId ? `environmentId=${environmentId}` : null,
                mode: this.requestOptions.mode
            });
            if (res.status === 200) {
                const response = yield res.json();
                const sessionCredentials = new PlatformCredentials_1.PlatformCredentials();
                sessionCredentials.fromSession(response);
                this.credentials = sessionCredentials;
                return sessionCredentials;
            }
            else {
                throw new Error(`Unable to obtain session Credentials ${res.status}`);
            }
        });
    }
    getModels() {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield (0, cross_fetch_1.default)(this.endpoint + `/models`, {
                method: 'GET',
                headers: {
                    'content-type': 'application/json',
                    Authorization: 'Bearer ' + this.credentials.accessToken
                },
                mode: this.requestOptions.mode
            });
            if (res.status === 200) {
                const response = yield res.json();
                return response;
            }
            else {
                throw new Error(res.statusText);
            }
        });
    }
    getModel(modelId, version) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield (0, cross_fetch_1.default)(this.endpoint + `/model/${modelId}/${version}`, {
                method: 'GET',
                headers: {
                    'content-type': 'application/json',
                    Authorization: 'Bearer ' + this.credentials.accessToken
                },
                mode: this.requestOptions.mode
            });
            if (res.status === 200) {
                const response = yield res.json();
                return response;
            }
            else {
                throw new Error(res.statusText);
            }
        });
    }
    getProvisionedModels(id, version) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield (0, cross_fetch_1.default)(this.endpoint + `/model/${id}/${version}/provisionedModels`, {
                method: 'GET',
                headers: {
                    'content-type': 'application/json',
                    Authorization: 'Bearer ' + this.credentials.accessToken
                },
                mode: this.requestOptions.mode
            });
            if (res.status === 200) {
                const response = yield res.json();
                return response;
            }
            else {
                throw new Error(res.statusText);
            }
        });
    }
    getProject(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield (0, cross_fetch_1.default)(this.endpoint + '/project/' + id, {
                method: 'GET',
                headers: {
                    Authorization: 'Bearer ' + this.credentials.accessToken
                },
                mode: this.requestOptions.mode
            });
            if (res.status === 200) {
                return res.json();
            }
            else {
                throw new Error(res.statusText);
            }
        });
    }
    requestModel(model, virtualizationProviderOverride, latencyOverride) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield (0, cross_fetch_1.default)(this.endpoint + `/model/${model.id}/${model.version}/launchrequests`, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    Authorization: 'Bearer ' + this.credentials.accessToken
                },
                mode: this.requestOptions.mode,
                body: JSON.stringify({
                    environmentId: this.credentials.environmentId,
                    virtualizationProviderOverride,
                    launchRequestInitiator: this.credentials.agent.rawToken,
                    latencies: virtualizationProviderOverride ? [] : latencyOverride !== null && latencyOverride !== void 0 ? latencyOverride : (yield this.getLatencyMeasurements())
                })
            });
            if (res.status === 202) {
                const response = yield res.json();
                return new LaunchRequest_1.LaunchRequest(response.id, { status: response.status }, response.access_token, model, response.projectId, this.credentials.accessToken, response.agentId);
            }
            else {
                // error_description matches what is return by the ModelLaunchRequestPost lambda in platform API
                throw (yield res.json()).error_description;
            }
        });
    }
    launchRequestAcknowledgements(kind, message) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield (0, cross_fetch_1.default)(this.endpoint + `/launchrequest/acknowledgements/${kind}`, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    Authorization: 'Bearer ' + this.credentials.agent.rawToken
                },
                mode: this.requestOptions.mode,
                body: JSON.stringify({
                    message
                })
            });
            if (res.status === 200) {
                return;
            }
            else {
                const { message } = yield res.json();
                throw new Error(`${res.statusText}: ${message}`);
            }
        });
    }
}
exports.PlatformNext = PlatformNext;
